import React, { useState, FormEvent, ChangeEvent } from "react"
import { AuthFlowFormField } from "./gql"

interface Props {
  fields: AuthFlowFormField[]
  description: string | null
  onSubmit: (values: Record<string, string>) => void
  isSubmitting: boolean
}

function getInputType(field: AuthFlowFormField): string {
  if (field.protected) {
    return "password"
  }

  const type = field.field_type?.toUpperCase()
  switch (type) {
    case "PASSWORD":
      return "password"
    case "URL":
      return "url"
    case "TEXT":
    default:
      return "text"
  }
}

const AuthFlowStepForm = ({ fields, description, onSubmit, isSubmitting }: Props) => {
  const [formValues, setFormValues] = useState<Record<string, string>>(() => {
    const values: Record<string, string> = {}
    fields.forEach(field => {
      values[field.name] = field.default_value || ""
    })
    return values
  })

  const handleInputChange = (fieldName: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(formValues)
  }

  const isFormValid = fields.every(field => !field.required || formValues[field.name])

  return (
    <form onSubmit={handleSubmit} className="auth-flow-step-form">
      {description && <p className="form-description">{description}</p>}

      <div className="form-fields">
        {fields.map((field, index) => (
          <div key={field.name} className="form-field">
            <label htmlFor={`auth-field-${field.name}`}>
              {field.prompt}
              {field.required && <span className="required"> *</span>}
            </label>
            <input
              type={getInputType(field)}
              id={`auth-field-${field.name}`}
              name={field.name}
              value={formValues[field.name] || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(field.name, e.target.value)}
              required={field.required}
              autoFocus={index === 0}
              autoComplete={field.protected || field.field_type === "PASSWORD" ? "off" : "on"}
              disabled={isSubmitting}
            />
            {field.helper_text && <span className="helper-text">{field.helper_text}</span>}
          </div>
        ))}
      </div>

      <div className="form-actions">
        <button type="submit" className="button-tertiary" disabled={!isFormValid || isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  )
}

export default AuthFlowStepForm
